import AbstractDBModel from './AbstractDBModel'

/** Lifecycle of an on-site hospitalization admission. */
export enum HospitalizationStatus {
  /** Screening under way; answers are saved after each step so it can be resumed. */
  screening = 'screening',
  /** Screening paused on ordered tests. Kept distinct so the banner can name what it waits on. */
  awaitingResults = 'awaitingResults',
  active = 'active',
  stopped = 'stopped',
  /** Screening was abandoned before the plan was activated. */
  cancelled = 'cancelled',
  completed = 'completed',
}

/** Screening that has begun but not yet produced an activated plan. */
export const IN_PROGRESS_STATUSES = [
  HospitalizationStatus.screening,
  HospitalizationStatus.awaitingResults,
]

/** One examination as it was planned (denormalised so the plan renders without the config). */
export interface HospitalizationPlanExam {
  /** Display label, e.g. "CBC" or "Ultrasound - Lung base". */
  label: string
  /** Device/order identity, so the plan can be matched back to created orders. */
  testValue: string
  testType?: string
  timesADay?: number
  everyXHours?: number
  note?: string
}

/** The activated protocol, captured at the moment of activation. */
export interface HospitalizationProtocol {
  totalDays: number
  /** Plans that span the whole admission (e.g. vitals twice daily). */
  recurring: HospitalizationPlanExam[]
  /** Day-specific examinations, keyed by 1-based protocol day. */
  byDay: { [day: string]: HospitalizationPlanExam[] }
  /** Non-examination staff tasks per day (i18n key suffixes under hospitalization.checklist.*). */
  checklistByDay: { [day: string]: string[] }
}

/** Everything the clinician answered during screening. */
export interface HospitalizationAnswersRecord {
  selectedDiagnoses: string[]
  consentSigned?: boolean
  /** Advance directives on file at the time of the decision (DNR/DNI), for the audit trail. */
  codeStatus: string[]
  redFlags: string[]
  readiness: { [key: string]: boolean }
}

/**
 * A record of one on-site hospitalization admission: what the clinician was asked, what they
 * answered, and the plan that was activated as a result.
 *
 * The document exists to make the decision auditable and to let the UI show the exact plan that
 * was activated rather than re-deriving it from the orders it produced.
 */
export default interface HospitalizationPlan extends AbstractDBModel {
  patientId: string
  /**
   * Denormalised at activation so the cross-patient hospitalization dashboard can render a list
   * from this collection alone, without a lookup per patient.
   */
  patientName?: string
  /** Facility / ward / room, likewise denormalised for the dashboard. */
  facilityName?: string
  room?: string
  status: HospitalizationStatus
  startedAt: string
  /** Set when the plan is activated; absent while screening is still in progress. */
  endsAt?: string
  answers: HospitalizationAnswersRecord
  protocol: HospitalizationProtocol
  /** Diagnoses attached to the created orders (real match, or the Z00.08 fallback). */
  patientDiagnoses: { diagnosisCode: string; diagnosisId?: string }[]
  /** How many orders the activation created. */
  ordersCreated?: number
  /**
   * Tests ordered during screening that the decision is waiting on (display labels).
   * Present while the record is in `awaitingResults` so any clinician opening the patient can see
   * why the admission is mid-decision.
   */
  pendingScreeningOrders?: string[]
  stoppedAt?: string
  stoppedBy?: string
  stopReason?: string
}
